import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hasMeaningfulAltText, picturesWithoutAltText, readPictures } from "@/office/word/media";

export const lektionAlternativtext: Lesson = {
  id: "alternativtext",
  title: "Alternativtexte",
  summary: "Was blinde Leserinnen und Leser von deinem Bild erfahren.",
  durationMinutes: 8,
  learningGoals: [
    "Alternativtexte setzen",
    "Beschreibende von nutzlosen Alternativtexten unterscheiden",
    "Dekorative Bilder richtig kennzeichnen",
  ],
  steps: [
    {
      id: "alt-text-setzen",
      title: "Den Alternativtext setzen",
      explanation: [
        "Ein Bild ohne Alternativtext existiert für einen Screenreader nicht. Wo Sehende " +
          "ein Diagramm mit den Quartalszahlen sehen, herrscht Stille – oder es wird der " +
          "Dateiname vorgelesen, was noch weniger hilft.",
        "Der Alternativtext beschreibt, was auf dem Bild zu sehen ist und warum es im " +
          "Dokument steht. Rechtsklick auf das Bild → „Alternativtext anzeigen“ öffnet " +
          "einen Bereich am Rand, in den du ein bis zwei Sätze schreibst.",
        "Word bietet dort auch „Alternativtext generieren lassen“ an. Das Ergebnis ist ein " +
          "brauchbarer Startpunkt, trifft den Zweck im Dokument aber selten – " +
          "überarbeite es.",
        "Für Behörden und öffentliche Stellen ist das in Deutschland keine Kür: Die " +
          "Barrierefreie-Informationstechnik-Verordnung schreibt zugängliche Dokumente " +
          "vor.",
      ],
      keyPoints: [
        "Rechtsklick auf das Bild → Alternativtext anzeigen.",
        "Beschreiben, was zu sehen ist und warum es dasteht.",
        "Ein Dateiname ist kein Alternativtext.",
      ],
      task: {
        instruction:
          "Gib deinem Bild einen aussagekräftigen Alternativtext – mindestens zwei Wörter, " +
          "kein Dateiname und kein bloßes „Bild 1“.",
        hint: "Beschreibe den Inhalt: „Balkendiagramm der Umsätze pro Quartal 2025“ statt „Diagramm“.",
        check: async () => {
          const bilder = await readPictures();
          if (bilder.length === 0) {
            return fail(
              "Im Dokument ist noch kein Bild eingefügt.",
              "Füge zuerst wie in der ersten Lektion ein Bild ein.",
            );
          }

          const ohne = picturesWithoutAltText(bilder);
          const brauchbar = bilder.filter(hasMeaningfulAltText);

          return fromCriteria(
            [
              criterion(`Jedes Bild hat einen Alternativtext (ohne: ${ohne.length})`, ohne.length === 0),
              criterion(
                `Mindestens ein Alternativtext ist beschreibend (erkannt: ${brauchbar.length})`,
                brauchbar.length >= 1,
              ),
            ],
            {
              success: `Gut – „${brauchbar[0]?.altText.slice(0, 60)}“ sagt tatsächlich etwas über das Bild aus.`,
              failure: "Der Alternativtext fehlt noch oder ist zu knapp.",
              hint: "Ein Wort, ein Dateiname oder „Bild 1“ wird nicht als Beschreibung gewertet. Schreibe einen kurzen Satz.",
            },
          );
        },
      },
    },
    {
      id: "dekorativ",
      title: "Dekorative Bilder",
      explanation: [
        "Nicht jedes Bild trägt Information. Eine Trennlinie, ein Zierrahmen oder ein " +
          "Hintergrundmuster sind reine Dekoration – für sie einen Alternativtext zu " +
          "erfinden, hilft niemandem und stört beim Vorlesen.",
        "Für diesen Fall gibt es im Alternativtext-Bereich das Kontrollkästchen „Als " +
          "dekorativ markieren“. Screenreader überspringen das Bild dann vollständig.",
        "Die Faustregel: Wenn du das Bild aus dem Dokument entfernen könntest, ohne dass " +
          "Information verloren geht, ist es dekorativ. Alles andere braucht eine " +
          "Beschreibung.",
      ],
      keyPoints: [
        "„Als dekorativ markieren“ im Alternativtext-Bereich.",
        "Faustregel: Geht ohne das Bild Information verloren? Dann beschreiben.",
        "Die Barrierefreiheitsprüfung unter „Überprüfen“ findet fehlende Alternativtexte.",
      ],
    },
  ],
};
